// Analytics and tracking utilities for lead capture and conversion tracking

declare global {
  interface Window {
    gtag?: (...args: unknown[]) => void;
    dataLayer?: unknown[];
  }
}

// Track page views
export const trackPageView = (path: string, title: string) => {
  if (typeof window !== 'undefined' && window.gtag) {
    window.gtag('config', 'AW-18229184969', {
      page_path: path,
      page_title: title
    });
  }
};

// Track custom events
export const trackEvent = (
  eventName: string,
  category: string,
  label?: string,
  value?: number
) => {
  if (typeof window !== 'undefined' && window.gtag) {
    window.gtag('event', eventName, {
      event_category: category,
      event_label: label,
      value: value
    });
  }
};

// Lead tracking events
export const trackLead = (leadType: string, source: string, industry?: string) => {
  trackEvent('generate_lead', 'Lead Generation', `${leadType} - ${source}`, 1);
  
  // Push to dataLayer for enhanced tracking
  if (typeof window !== 'undefined' && window.dataLayer) {
    window.dataLayer.push({
      event: 'lead_captured',
      lead_type: leadType,
      lead_source: source,
      industry: industry || 'not_specified',
      timestamp: new Date().toISOString()
    });
  }
};

// CTA click tracking
export const trackCTAClick = (ctaName: string, location: string) => {
  trackEvent('cta_click', 'Engagement', `${ctaName} - ${location}`);
};

// Form submission tracking
export const trackFormSubmission = (formName: string, success: boolean) => {
  trackEvent(
    success ? 'form_submit_success' : 'form_submit_error',
    'Form Submissions',
    formName
  );
};

// Resource download tracking
export const trackDownload = (resourceName: string, resourceType: string) => {
  trackEvent('file_download', 'Downloads', `${resourceType}: ${resourceName}`);
};

// Newsletter subscription tracking
export const trackNewsletterSignup = (email: string) => {
  trackEvent('newsletter_signup', 'Lead Generation', 'Newsletter');
  trackLead('newsletter', 'homepage');
};

// Demo request tracking
export const trackDemoRequest = (source: string) => {
  trackEvent('demo_request', 'Lead Generation', source);
  trackLead('demo', source);
};

// Consultation request tracking
export const trackConsultationRequest = (source: string, industry?: string) => {
  trackEvent('consultation_request', 'Lead Generation', source);
  trackLead('consultation', source, industry);
};

// Product interest tracking
export const trackProductInterest = (productName: string) => {
  trackEvent('product_view', 'Product Interest', productName);
};

// Scroll depth tracking
export const trackScrollDepth = (depth: number, pageName: string) => {
  if (depth === 25 || depth === 50 || depth === 75 || depth === 100) {
    trackEvent('scroll_depth', 'Engagement', `${pageName} - ${depth}%`);
  }
};

// Time on page tracking
export const trackTimeOnPage = (seconds: number, pageName: string) => {
  if (seconds === 30 || seconds === 60 || seconds === 120 || seconds === 300) {
    trackEvent('time_on_page', 'Engagement', `${pageName} - ${seconds}s`);
  }
};
